import { CloseOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { PageLayoutWithHelp } from "@features/page-help";
import {
  type MessageTemplate,
  useDeleteTemplate,
  useTemplates,
  useUpsertTemplate,
} from "@features/templates/api";
import TemplateForm from "@features/templates/components/TemplateForm";
import { templateColumns } from "@features/templates/config/columns";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import EnterpriseDrawer from "@shared/ui/enterprise-drawer";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { App as AntdApp, Button, Flex, Form, Tooltip } from "antd";
import { useState } from "react";

const AdminTemplatesPage = () => {
  const { message } = AntdApp.useApp();
  const { data: templates, isLoading } = useTemplates();
  const upsertTemplate = useUpsertTemplate();
  const deleteTemplate = useDeleteTemplate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<MessageTemplate | null>(null);
  const [form] = Form.useForm();

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (tpl: MessageTemplate) => {
    setEditing(tpl);
    setDrawerOpen(true);
  };

  const handleClose = () => setDrawerOpen(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await upsertTemplate.mutateAsync({ ...values, id: editing?.id ?? "" });
      message.success("Сохранено");
      setDrawerOpen(false);
    } catch (err: unknown) {
      message.error(extractErrorMessage(err));
    }
  };

  const columns = templateColumns(openEdit, (id) => deleteTemplate.mutate({ id }));

  return (
    <PageLayoutWithHelp
      title="Шаблоны сообщений"
      extra={
        <Tooltip title="Создать шаблон">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Создать
          </Button>
        </Tooltip>
      }
    >
      <EnterpriseTable
        columns={columns}
        dataSource={templates ?? []}
        rowKey="id"
        loading={isLoading}
      />
      <EnterpriseDrawer
        title={editing ? "Редактирование" : "Новый шаблон"}
        open={drawerOpen}
        onClose={handleClose}
        size="middle"
        destroyOnHidden
        actions={
          <Flex gap={8}>
            <Tooltip title="Отменить">
              <Button icon={<CloseOutlined />} onClick={handleClose} />
            </Tooltip>
            <Tooltip title="Сохранить">
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSubmit}
                loading={upsertTemplate.isPending}
              />
            </Tooltip>
          </Flex>
        }
      >
        <TemplateForm form={form} initialData={editing} />
      </EnterpriseDrawer>
    </PageLayoutWithHelp>
  );
};

export default AdminTemplatesPage;
